import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { networkManager } from '../services/NetworkManager';
import { NewBuildRequest, Definition, DataSource } from '../types';
import { useNotification } from '../contexts/NotificationContext';

const NewBuildPage: React.FC = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const definitionDropdownRef = useRef<HTMLDivElement>(null);
  const datasourceDropdownRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState<NewBuildRequest>({
    title: '',
    description: '',
    datasourceId: '',
    DefinitionID: '',
    BuildJSON: false
  });
  const [loading, setLoading] = useState(false);
  const [errorBuildId, setErrorBuildId] = useState<string | null>(null);
  const [definitions, setDefinitions] = useState<Definition[]>([]);
  const [loadingDefinitions, setLoadingDefinitions] = useState(true);
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [loadingDataSources, setLoadingDataSources] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDefinition, setSelectedDefinition] = useState<Definition | null>(null);
  const [showDefinitionDropdown, setShowDefinitionDropdown] = useState(false);
  const [datasourceSearchTerm, setDatasourceSearchTerm] = useState('');
  const [selectedDataSource, setSelectedDataSource] = useState<DataSource | null>(null);
  const [showDatasourceDropdown, setShowDatasourceDropdown] = useState(false);

  // Load definitions and datasources on component mount
  useEffect(() => {
    loadDefinitions();
    loadDataSources();
  }, []);

  // Handle clicking outside dropdowns to close them
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (definitionDropdownRef.current && !definitionDropdownRef.current.contains(event.target as Node)) {
        setShowDefinitionDropdown(false);
      }
      if (datasourceDropdownRef.current && !datasourceDropdownRef.current.contains(event.target as Node)) {
        setShowDatasourceDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadDefinitions = async () => {
    try {
      setLoadingDefinitions(true);
      const data = await networkManager.getDefinitions();
      setDefinitions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading definitions:', err);
      showNotification('Failed to load definitions', 'error', 8000);
    } finally {
      setLoadingDefinitions(false);
    }
  };

  const loadDataSources = async () => {
    try {
      setLoadingDataSources(true);
      const data = await networkManager.getDataSources();
      setDataSources(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading data sources:', err);
      showNotification('Failed to load data sources', 'error', 8000);
    } finally {
      setLoadingDataSources(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDefinitionSearch = (searchValue: string) => {
    setSearchTerm(searchValue);
    setShowDefinitionDropdown(true);
    
    if (searchValue.trim() === '') {
      setSelectedDefinition(null);
      setFormData(prev => ({ ...prev, DefinitionID: '' }));
      return;
    }

    // Find definition by title (case-insensitive)
    const found = definitions.find(def => 
      def.title.toLowerCase().includes(searchValue.toLowerCase())
    );
    
    if (found) {
      setSelectedDefinition(found);
      setFormData(prev => ({ ...prev, DefinitionID: found._id }));
    } else {
      setSelectedDefinition(null);
      setFormData(prev => ({ ...prev, DefinitionID: '' }));
    }
  };

  const handleDefinitionSelect = (definition: Definition) => {
    setSelectedDefinition(definition);
    setSearchTerm(definition.title);
    setFormData(prev => ({ ...prev, DefinitionID: definition._id }));
    setShowDefinitionDropdown(false);
  };

  const handleDefinitionInputFocus = () => {
    setShowDefinitionDropdown(true);
  };

  const handleDataSourceSearch = (searchValue: string) => {
    setDatasourceSearchTerm(searchValue);
    setShowDatasourceDropdown(true);
    
    if (searchValue.trim() === '') {
      setSelectedDataSource(null);
      setFormData(prev => ({ ...prev, datasourceId: '' }));
      return;
    }

    // Find datasource by title (case-insensitive)
    const found = dataSources.find(ds => 
      ds.title.toLowerCase().includes(searchValue.toLowerCase())
    );
    
    if (found) {
      setSelectedDataSource(found);
      setFormData(prev => ({ ...prev, datasourceId: found._id }));
    } else {
      setSelectedDataSource(null);
      setFormData(prev => ({ ...prev, datasourceId: '' }));
    }
  };

  const handleDataSourceSelect = (dataSource: DataSource) => {
    setSelectedDataSource(dataSource);
    setDatasourceSearchTerm(dataSource.title);
    setFormData(prev => ({ ...prev, datasourceId: dataSource._id }));
    setShowDatasourceDropdown(false);
  };

  const handleDataSourceInputFocus = () => {
    setShowDatasourceDropdown(true);
  };

  const filteredDefinitions = searchTerm.trim() === '' 
    ? definitions.slice(0, 5) // Show last 5 definitions when no search term
    : definitions.filter(def =>
        def.title.toLowerCase().includes(searchTerm.toLowerCase())
      ).slice(0, 5); // Limit to 5 results

  const filteredDataSources = datasourceSearchTerm.trim() === '' 
    ? dataSources.slice(0, 5) // Show last 5 datasources when no search term
    : dataSources.filter(ds =>
        ds.title.toLowerCase().includes(datasourceSearchTerm.toLowerCase())
      ).slice(0, 5); // Limit to 5 results

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      showNotification('Please enter a build title', 'error', 5000);
      return;
    }

    if (!formData.datasourceId.trim()) {
      showNotification('Please select a datasource', 'error', 5000);
      return;
    }

    if (!formData.DefinitionID.trim()) {
      showNotification('Please select a definition', 'error', 5000);
      return;
    }

    try {
      setLoading(true);
      setErrorBuildId(null);

      const newBuild = await networkManager.createDataBuild(formData);
      console.log('Build created successfully:', newBuild);
      
      // Extract build ID from the response
      const buildId = (newBuild as any).buildId || (newBuild as any)._id;
      
      console.log('Extracted build ID:', buildId);
      
      if (buildId) {
        // Redirect directly to build details
        navigate(`/data-builds/${buildId}`);
      } else {
        console.error('No build ID found in response:', newBuild);
        showNotification('Build created but no ID returned. Please check the builds list.', 'error', 8000);
      }
    } catch (err: any) {
      console.error('Error creating build:', err);
      console.error('Error object:', err);
      console.error('Error message:', err.message);
      
      // Try to extract buildId and message from error response
      let buildId = null;
      let serverMessage = 'Failed to create new build';
      
      // First, check if buildId is directly in the error object
      if ((err as any).buildId) {
        buildId = (err as any).buildId;
      }
      
      // Also check if the error has a data property with buildId
      if (!buildId && (err as any).data && (err as any).data.buildId) {
        buildId = (err as any).data.buildId;
      }
      
      if (err.message) {
        try {
          const errorData = JSON.parse(err.message);
          console.log('Parsed error data:', errorData);
          
          if (errorData.data && errorData.data.buildId) {
            buildId = errorData.data.buildId;
          }
          // Also check if buildId is at the root level
          if (!buildId && errorData.buildId) {
            buildId = errorData.buildId;
          }
          if (errorData.message) {
            serverMessage = errorData.message;
          }
        } catch (parseError) {
          console.error('Error parsing error response:', parseError);
          console.log('Original error message (not JSON):', err.message);
          serverMessage = err.message;
          
          // Try to extract buildId from the error message string if it contains JSON-like structure
          // This handles cases where the error might be partially formatted
          try {
            // Look for buildId pattern in the message
            const buildIdMatch = err.message.match(/buildId["\s:]+["']?([a-f0-9]{24})["']?/i);
            if (buildIdMatch && buildIdMatch[1]) {
              buildId = buildIdMatch[1];
              console.log('Extracted buildId from message pattern:', buildId);
            }
          } catch (extractError) {
            console.error('Error extracting buildId from message:', extractError);
          }
        }
      }
      
      console.log('Final buildId:', buildId);
      console.log('Final serverMessage:', serverMessage);
      
      if (buildId) {
        // Show notification with link to check build logs
        const buildUrl = `/data-builds/${buildId}`;
        const notificationContent = (
          <div>
            <div className="mb-2">{serverMessage}</div>
            <a
              href={buildUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 underline font-semibold"
              onClick={(e) => {
                e.preventDefault();
                window.open(buildUrl, '_blank', 'noopener,noreferrer');
              }}
            >
              Check build logs
            </a>
          </div>
        );
        showNotification(notificationContent, 'error', 10000);
      } else {
        showNotification(serverMessage, 'error', 8000);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Create New Build</h1>
          <p className="text-gray-600 mt-1">Configure and create a new data build</p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/data-builds')}
          className="btn btn-secondary"
        >
          <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Builds
        </button>
      </div>



      {/* Form */}
      <div className="card p-8">
        <div className="flex items-center mb-6">
          <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
            <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Build Configuration</h2>
            <p className="text-sm text-gray-600">Fill in the information below to create your data build</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                Build Title *
              </label>
              <input
                type="text"
                id="title"
                name="title"
                required
                value={formData.title}
                onChange={handleInputChange}
                className="input text-base"
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                rows={4}
                value={formData.description}
                onChange={handleInputChange}
                className="input resize-none text-base"
                placeholder="Enter build description"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="relative" ref={datasourceDropdownRef}>
              <label htmlFor="datasourceSearch" className="block text-sm font-medium text-gray-700 mb-2">
                Datasource *
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="datasourceSearch"
                  value={datasourceSearchTerm}
                  onChange={(e) => handleDataSourceSearch(e.target.value)}
                  onFocus={handleDataSourceInputFocus}
                  className="input text-base w-full"
                  placeholder={loadingDataSources ? "Loading datasources..." : "Search for a datasource"}
                  disabled={loadingDataSources}
                  autoComplete="off"
                />
                {loadingDataSources && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="spinner h-4 w-4"></div>
                  </div>
                )}
              </div>

              {/* Datasource Dropdown */}
              {showDatasourceDropdown && !loadingDataSources && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredDataSources.length > 0 ? (
                    <>
                      {datasourceSearchTerm.trim() === '' && (
                        <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                          <p className="text-xs font-medium text-gray-600">Recent Datasources</p>
                        </div>
                      )}
                      {filteredDataSources.map((dataSource) => (
                        <div
                          key={dataSource._id}
                          onClick={() => handleDataSourceSelect(dataSource)}
                          className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium text-gray-900">{dataSource.title}</div>
                        </div>
                      ))}
                    </>
                  ) : datasourceSearchTerm.trim() !== '' ? (
                    <div className="px-4 py-3 text-gray-500">
                      No datasources found matching "{datasourceSearchTerm}"
                    </div>
                  ) : (
                    <div className="px-4 py-3 text-gray-500">
                      No datasources available
                    </div>
                  )}
                </div>
              )}

              {/* Selected Datasource Display */}
              {selectedDataSource && (
                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <svg className="h-5 w-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-green-800">{selectedDataSource.title}</p>
                        <p className="text-xs text-green-600">ID: {selectedDataSource._id}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedDataSource(null);
                        setDatasourceSearchTerm('');
                        setFormData(prev => ({ ...prev, datasourceId: '' }));
                      }}
                      className="text-red-600 hover:text-red-800 transition-colors"
                      title="Remove datasource"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="relative" ref={definitionDropdownRef}>
              <label htmlFor="definitionSearch" className="block text-sm font-medium text-gray-700 mb-2">
                Definition *
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="definitionSearch"
                  value={searchTerm}
                  onChange={(e) => handleDefinitionSearch(e.target.value)}
                  onFocus={handleDefinitionInputFocus}
                  className="input text-base w-full"
                  placeholder={loadingDefinitions ? "Loading definitions..." : "Search for a definition"}
                  disabled={loadingDefinitions}
                  autoComplete="off"
                />
                {loadingDefinitions && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="spinner h-4 w-4"></div>
                  </div>
                )}
              </div>

              {/* Definition Dropdown */}
              {showDefinitionDropdown && !loadingDefinitions && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredDefinitions.length > 0 ? (
                    <>
                      {searchTerm.trim() === '' && (
                        <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                          <p className="text-xs font-medium text-gray-600">Recent Definitions</p>
                        </div>
                      )}
                      {filteredDefinitions.map((definition) => (
                        <div
                          key={definition._id}
                          onClick={() => handleDefinitionSelect(definition)}
                          className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium text-gray-900">{definition.title}</div>
                          <div className="text-sm text-gray-500 truncate">{definition.description}</div>
                        </div>
                      ))}
                    </>
                  ) : searchTerm.trim() !== '' ? (
                    <div className="px-4 py-3 text-gray-500">
                      No definitions found matching "{searchTerm}"
                    </div>
                  ) : (
                    <div className="px-4 py-3 text-gray-500">
                      No definitions available
                    </div>
                  )}
                </div>
              )}

              {/* Selected Definition Display */}
              {selectedDefinition && (
                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <svg className="h-5 w-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-green-800">{selectedDefinition.title}</p>
                        <p className="text-xs text-green-600">ID: {selectedDefinition._id}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedDefinition(null);
                        setSearchTerm('');
                        setFormData(prev => ({ ...prev, DefinitionID: '' }));
                      }}
                      className="text-red-600 hover:text-red-800 transition-colors"
                      title="Remove definition"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="pt-4">
            <button
              type="submit"
              disabled={loading || !formData.title || !formData.datasourceId || !formData.DefinitionID}
              className="btn btn-primary w-full py-3 text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="spinner h-5 w-5 mr-2"></div>
                  Creating Build...
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Create Build
                </div>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewBuildPage;
